import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { FaCogs } from 'react-icons/fa';
import { IoMdPie } from 'react-icons/io';
import { PropagateLoader } from 'react-spinners';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

import BreadCrumb from '~/components/BreadCrumb';
import Badge from '~/components/Badge';
import CloseButton from '~/components/Buttons/Close';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import DefaultInput from '~/components/Inputs/Default';
import DetailButton from '~/components/Buttons/Detail';
import ExitButton from '~/components/Buttons/Exit';

import { getFormattedCurrency } from '~/utils';

import api from '~/services/api';

import {
  Container,
  Table,
  Td,
  ListCenterComp,
  MovementPrice,
  StyledModal,
} from './styles';

interface IGroup {
  id: string;
  name: string;
  total_price: number;
  used_price: number;
  rebate_balance_id: string;
}

const View: React.FC = () => {
  const history = useHistory();
  const { state } = useLocation<{ id: string; code: string; open: boolean }>();
  const [open, setOpen] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [group, setGroup] = useState<{
    name: string;
    isOpen: boolean;
    loading: boolean;
    movements: {
      project: {
        code: string;
        status: string;
        user: string;
      };
      date: Date;
      price: number;
    }[];
  }>({
    name: '',
    isOpen: false,
    loading: true,
    movements: [],
  });
  const [isOpenExit, setIsOpenExit] = useState(false);

  useEffect(() => {
    const loadGroups = async () => {
      await api.get(`/quarters/balances/${state.id}`).then(response => {
        setOpen(response.data.open);

        setGroups(
          response.data.rebate_balances.map(
            (rebate_balance: {
              id: string;
              total_price: number;
              used_price: number;
              policy_group: { id: string; name: string };
            }) => ({
              id: rebate_balance.policy_group.id,
              name: rebate_balance.policy_group.name,
              total_price: rebate_balance.total_price,
              used_price: rebate_balance.used_price,
              rebate_balance_id: rebate_balance.id,
            }),
          ),
        );

        setLoadingGroups(false);
      });
    };

    if (state && loadingGroups) {
      loadGroups();
    }
  }, [loadingGroups, state]);

  const handleGroupMovements = (rebate_balance_id: string, name: string) => {
    api
      .get(
        `/rebatemovements/rebatebalance?rebate_balance_id=${rebate_balance_id}`,
      )
      .then(response => {
        setGroup({
          name,
          isOpen: true,
          loading: false,
          movements: response.data,
        });
      });
  };

  return (
    <Container>
      <BreadCrumb
        main="Configurações"
        path="/configurations/main"
        firstChildPath="/configurations/rebatebalancequarters"
        firstChild="Saldos Rebate - Trimestres"
        secondChild="Visualização de Trimestre"
        mainIcon={FaCogs}
        firstChildIcon={IoMdPie}
        secondChildIcon={IoMdPie}
        mainColored
        firstChildColored
      />

      <form>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
          <div style={{ width: 400 }}>
            <DefaultInput
              name="code"
              labelFor="code"
              labelText="Código"
              type="text"
              defaultValue={state?.code}
              disabled
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Badge
              label={open ? 'Aberto' : 'Fechado'}
              type={open ? 'positive' : 'negative'}
            />
          </div>
        </div>

        <div
          style={{ marginTop: 30, display: 'flex', justifyContent: 'center' }}
        >
          <Table>
            <thead>
              <tr>
                <th colSpan={5} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontStyle: 'italic' }}>
                    Grupos
                  </div>
                </th>
              </tr>
              <tr>
                <th style={{ width: '20%', textAlign: 'left' }}>Nome</th>
                <th style={{ width: '20%', textAlign: 'right' }}>
                  Valor Total
                </th>
                <th style={{ width: '20%', textAlign: 'right' }}>
                  Valor Utilizado
                </th>
                <th style={{ width: '20%', textAlign: 'right' }}>
                  Valor Pendente
                </th>
                <th style={{ width: '20%', textAlign: 'center' }} />
              </tr>
            </thead>

            <tbody>
              {loadingGroups ? (
                <tr>
                  <Td
                    colSpan={5}
                    style={{ padding: '3rem', textAlign: 'center' }}
                  >
                    <PropagateLoader color="#276c8d" />
                  </Td>
                </tr>
              ) : groups.length > 0 ? (
                groups
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(grp => (
                    <tr key={grp.id}>
                      <Td
                        style={{ width: '20%', textAlign: 'left' }}
                        heading="NOME"
                      >
                        {grp.name}
                      </Td>

                      <Td
                        style={{ width: '20%', textAlign: 'right' }}
                        heading="VALOR TOTAL"
                      >
                        {getFormattedCurrency(grp.total_price)}
                      </Td>

                      <Td
                        style={{ width: '20%', textAlign: 'right' }}
                        heading="VALOR UTILIZADO"
                      >
                        {getFormattedCurrency(grp.used_price)}
                      </Td>

                      <Td
                        style={{ width: '20%', textAlign: 'right' }}
                        heading="VALOR PENDENTE"
                      >
                        {getFormattedCurrency(grp.total_price - grp.used_price)}
                      </Td>

                      <Td style={{ width: '20%' }} heading="">
                        <ListCenterComp>
                          <DetailButton
                            type="button"
                            onClick={() => {
                              handleGroupMovements(
                                grp.rebate_balance_id,
                                grp.name,
                              );
                            }}
                          />
                        </ListCenterComp>
                      </Td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <Td colSpan={5} style={{ textAlign: 'center' }}>
                    Nenhum grupo disponível
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div
          style={{ marginTop: 50, display: 'flex', justifyContent: 'flex-end' }}
        >
          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <StyledModal
        isOpen={group.isOpen}
        style={{
          overlay: {
            zIndex: 999,
            overflowY: 'auto',
            position: 'fixed',
            background: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <div style={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              fontSize: 20,
              fontStyle: 'italic',
              fontWeight: 'bold',
            }}
          >
            {`Movimentações do grupo ${group.name} durante o trimestre ${state.code}`}
          </div>

          <div>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '25%', textAlign: 'left' }}>Data</th>
                  <th style={{ width: '25%', textAlign: 'left' }}>
                    Solicitação
                  </th>
                  <th style={{ width: '25%', textAlign: 'left' }}>Usuário</th>
                  <th style={{ width: '25%', textAlign: 'center' }}>Valor</th>
                </tr>
              </thead>

              <tbody>
                {group.loading ? (
                  <tr>
                    <Td
                      colSpan={4}
                      style={{ padding: '3rem', textAlign: 'center' }}
                    >
                      <PropagateLoader color="#276c8d" />
                    </Td>
                  </tr>
                ) : group.movements.length > 0 ? (
                  group.movements.map(grp => (
                    <tr key={uuidv4()}>
                      <Td
                        style={{ width: '25%', textAlign: 'left' }}
                        heading="DATA"
                      >
                        {moment(grp.date).format('DD/MM/YYYY')}
                      </Td>

                      <Td
                        style={{ width: '25%', textAlign: 'left' }}
                        heading="SOLICITAÇÃO"
                      >
                        {grp.project.code}
                      </Td>

                      <Td
                        style={{ width: '25%', textAlign: 'left' }}
                        heading="USUÁRIO"
                      >
                        {grp.project.user}
                      </Td>

                      <Td
                        style={{ width: '25%', textAlign: 'right' }}
                        heading="VALOR"
                      >
                        <MovementPrice
                          negative={
                            grp.project.status.toUpperCase() === 'CANCELADO'
                          }
                        >
                          {`${
                            grp.project.status.toUpperCase() === 'CANCELADO'
                              ? '- '
                              : ''
                          }${getFormattedCurrency(grp.price)}`}
                        </MovementPrice>
                      </Td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <Td colSpan={4} style={{ textAlign: 'center' }}>
                      Nenhuma movimentação realizada
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div
            style={{
              margin: 20,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CloseButton
              type="button"
              onClick={() => {
                setGroup({
                  name: '',
                  isOpen: false,
                  loading: true,
                  movements: [],
                });
              }}
            />
          </div>
        </div>
      </StyledModal>

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={() => history.push('/configurations/rebatebalancequarters')}
      />
    </Container>
  );
};

export default View;
